/* eslint-disable max-lines */
import { useEffect, useContext, useState } from 'react';
import {
  Row,
  Col,
  Button,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Badge,
} from 'reactstrap';
import NumberFormat from 'react-number-format';
import ReactDatetime from 'react-datetime';
import moment from 'moment';
import Employee from '../../../services/Employee';
import PerformanceManagement from '../../../services/PerformanceManagement';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import LoaderSpinner from '../../Core/LoaderSpinner';
import { getFormValues } from '../../../helpers/forms';
import { toast } from 'react-toastify';
import { createOptionsPage } from '../../../entities/OptionsPaged';
import { createPagedMeta } from '../../../entities/PagedMeta';

interface PayRateFormInterface {
  id?: string;
  onSubmit?: (r: any) => void;
  employeeData?: any;
  defaultReason?: string;
  defaultPayRate?: any;
  defaultEffectiveAt?: string;
}

const PayRatesForm = ({
  id,
  onSubmit = (_: any) => {},
  employeeData,
  defaultReason,
  defaultPayRate,
  defaultEffectiveAt,
}: PayRateFormInterface) => {
  const { token }: any = useContext(AuthContext);
  const [employee, setEmployee] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [payRate, setPayRate] = useState<any>(defaultPayRate ?? 0);
  const [effectiveAt, setEffectiveAt] = useState<string>(defaultEffectiveAt ?? '');
  const onlyShowMode = !!employeeData;
  const calcPayRateIncrease = (
    (parseFloat(payRate) / parseFloat(employee?.currentPayRate?.payRate)) * 100 -
    100
  ).toFixed(2);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setLoading(true);
      const body = getFormValues(e.target);
      body.payRate = payRate != null && !isNaN(parseFloat(payRate)) ? parseFloat(payRate) : null;
      body.effectiveAt = effectiveAt;
      const response = await Employee(token).updatePayRate(id || employee?.employee?.id, body);
      onSubmit(response);
      toast.success('Saved Successfully!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        setLoading(true);
        const [employeeRes, disciplineCounts] = await Promise.all([
          Employee(token).show(id),
          PerformanceManagement(token)
            .getDisciplineCountsByEmployee(id)
            .then((r: any) => r?.data)
            .catch(() => null),
        ]);
        const data = employeeRes?.data || {};
        setEmployee({
          ...data,
          ...(disciplineCounts
            ? {
                performaceImprovementPlan:
                  disciplineCounts.performanceImprovementCount ?? data.performaceImprovementPlan,
                verbalWarnings:
                  disciplineCounts.verbalWarningCount != null
                    ? String(disciplineCounts.verbalWarningCount)
                    : data.verbalWarnings,
                disciplinaryActionNotices:
                  disciplineCounts.disciplinaryActionCount != null
                    ? String(disciplineCounts.disciplinaryActionCount)
                    : data.disciplinaryActionNotices,
              }
            : {}),
          proposedPayRate: { paymentFrequency: data?.currentPayRate?.paymentFrequency },
        });
        setEffectiveAt(data?.effectiveAt || '');
      } catch (e) {
        console.error('Failed to load employee');
        setEmployee(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, token, setEmployee]);

  useEffect(() => {
    if (!employeeData) return;
    const load = async () => {
      try {
        setLoading(true);
        const employee = await Employee(token).show(employeeData.employeeId);

        let currentPayRateValue: any = null;

        if (employeeData?.status?.toLowerCase() !== 'waiting' && employeeData?.percentIncrease) {
          const proposedPayRate = parseFloat(employeeData.payRate) || 0;
          const percentIncrease = parseFloat(employeeData.percentIncrease) || 0;
          if (percentIncrease > 0 && proposedPayRate > 0) {
            currentPayRateValue = {
              payRate: proposedPayRate / (1 + percentIncrease / 100),
              paymentFrequency: employeeData?.paymentFrequency,
            };
          }
        } else {
          try {
            const payRatesResponse = await Employee(token).listPayRates(employeeData.employeeId, {
              filters: [],
              options: createOptionsPage(createPagedMeta({ perPage: 999999 })),
            });

            if (payRatesResponse?.data && Array.isArray(payRatesResponse.data)) {
              const currentRecordDate = moment(employeeData.createdAt);
              const currentRecordId = employeeData.id;

              const previousPayRates = payRatesResponse.data
                .filter((pr: any) => {
                  if (pr.id === currentRecordId) return false;

                  const status = pr.status?.toLowerCase();
                  const isApprovedOrChanged = status === 'approved' || status === 'changed';
                  if (!isApprovedOrChanged) return false;

                  if (!pr.createdAt) return false;

                  const prCreatedDate = moment(pr.createdAt);
                  return prCreatedDate.isBefore(currentRecordDate);
                })
                .sort((a: any, b: any) => {
                  const aDate = moment(a.createdAt);
                  const bDate = moment(b.createdAt);
                  return bDate.valueOf() - aDate.valueOf();
                });

              if (previousPayRates.length > 0) {
                const sameFrequencyPayRate = previousPayRates.find(
                  (pr: any) => pr.paymentFrequency === employeeData.paymentFrequency && pr.payRate != null,
                );

                if (sameFrequencyPayRate) {
                  currentPayRateValue = {
                    payRate: parseFloat(sameFrequencyPayRate.payRate),
                    paymentFrequency: sameFrequencyPayRate.paymentFrequency,
                  };
                } else {
                  const anyPreviousPayRate = previousPayRates.find((pr: any) => pr.payRate != null);
                  if (anyPreviousPayRate) {
                    currentPayRateValue = {
                      payRate: parseFloat(anyPreviousPayRate.payRate),
                      paymentFrequency: anyPreviousPayRate.paymentFrequency,
                    };
                  }
                }
              }
            }
          } catch (e) {
            console.error('Failed to load previous pay rates');
          }
        }

        setEmployee({
          ...employeeData,
          ...{
            user: employee?.data?.user,
            currentPayRate: currentPayRateValue,
            proposedPayRate: {
              paymentFrequency: employeeData?.paymentFrequency,
              payRate:
                employeeData?.payRate != null && !isNaN(parseFloat(employeeData.payRate))
                  ? parseFloat(employeeData.payRate)
                  : null,
            },
          },
        });
        setPayRate(employeeData?.payRate != null ? employeeData.payRate : 0);
        setEffectiveAt(employeeData?.effectiveAt || '');
      } catch (e) {
        console.error('Failed to load employee');
        setEmployee(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [employeeData, token, setEmployee]);

  return (
    <>
      {loading && (
        <Row>
          <Col style={{ minHeight: '100px' }}>
            <LoaderSpinner />
          </Col>
        </Row>
      )}
      {!loading && (
        <form onSubmit={(e) => handleSubmit(e)}>
          <Row>
            <Col className="mb-4">
              <h3>Update Employee Pay Rate</h3>
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <FormGroup className="form-control-label">
                <Label>Employee First Name</Label>
                <Input
                  type="text"
                  className="form-control-alternative"
                  name="user[firstName]"
                  defaultValue={employee?.user?.firstName}
                  disabled
                />
              </FormGroup>
            </Col>
            <Col sm={6}>
              <FormGroup className="form-control-label">
                <Label>Employee Last Name</Label>
                <Input
                  type="text"
                  className="form-control-alternative"
                  name="user[lastName]"
                  defaultValue={employee?.user?.lastName}
                  disabled
                />
              </FormGroup>
            </Col>
            {onlyShowMode && (
              <Col sm={12}>
                <FormGroup className="form-control-label">
                  <Label>Date</Label>
                  <Input
                    type="text"
                    className="form-control-alternative"
                    value={employee?.createdAt ? moment(employee.createdAt).format('MM/DD/YYYY') : '-'}
                    disabled
                  />
                </FormGroup>
              </Col>
            )}
            <Col sm={12}>
              <FormGroup>
                <legend>Current</legend>
                <Row>
                  <Col>
                    <FormGroup className="form-control-label">
                      <Label>Payment Frequency</Label>
                      <Input
                        type="select"
                        className="form-control-alternative"
                        name="paymentFrequency"
                        defaultValue={employee?.currentPayRate?.paymentFrequency}
                        disabled
                      >
                        <option value=""></option>
                        <option value={'hourly'}>Hourly</option>
                        <option value={'annually'}>Annually</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup className="form-control-label">
                      <Label>Pay Rate</Label>
                      {employee?.currentPayRate?.payRate != null ? (
                        <Input
                          type="text"
                          thousandsGroupStyle="thousand"
                          prefix="$"
                          decimalSeparator="."
                          displayType="input"
                          thousandSeparator={true}
                          allowNegative={false}
                          decimalScale={2}
                          tag={NumberFormat}
                          value={employee.currentPayRate.payRate}
                          disabled
                        />
                      ) : (
                        <Input type="text" value="-" disabled className="form-control-alternative" />
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </FormGroup>
            </Col>

            <Col sm={12}>
              <FormGroup>
                <legend>Proposed</legend>
                <Row>
                  <Col>
                    <FormGroup className="form-control-label">
                      <Label>Payment Frequency</Label>
                      <Input
                        type="select"
                        className="form-control-alternative"
                        name="paymentFrequency"
                        defaultValue={employee?.proposedPayRate?.paymentFrequency}
                        required
                        disabled={onlyShowMode}
                      >
                        <option value=""></option>
                        <option value={'hourly'}>Hourly</option>
                        <option value={'annually'}>Annually</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup className="form-control-label">
                      <Label>Pay Rate</Label>
                      <InputGroup className="input-group-alternative">
                        <Input
                          type="text"
                          name="payRate"
                          className="form-control-alternative"
                          thousandsGroupStyle="thousand"
                          prefix="$"
                          decimalSeparator="."
                          displayType="input"
                          thousandSeparator={true}
                          allowNegative={false}
                          decimalScale={2}
                          tag={NumberFormat}
                          value={payRate}
                          onValueChange={(e: any) => setPayRate(e.floatValue)}
                          disabled={onlyShowMode}
                          required
                        />
                        {parseFloat(calcPayRateIncrease) > 0 && (
                          <InputGroupAddon addonType="append">
                            <InputGroupText>
                              <Badge color="success" className="text-right">
                                {calcPayRateIncrease}%
                              </Badge>
                            </InputGroupText>
                          </InputGroupAddon>
                        )}
                      </InputGroup>
                    </FormGroup>
                  </Col>
                </Row>
              </FormGroup>
            </Col>
            <Col sm={12}>
              <FormGroup className="form-control-label">
                <Label>Reason</Label>
                <Input
                  type="select"
                  name="reason"
                  required
                  defaultValue={employee.reason || defaultReason || ''}
                  disabled={onlyShowMode}
                >
                  <option value=""></option>
                  {['Annual', 'Promotion', 'Education', 'Other'].map((value) => (
                    <option key={`reason-${value}`} value={value}>
                      {value}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col sm={12}>
              <FormGroup className="form-control-label">
                <Label>Rate Attendance</Label>
                <Row>
                  <Col className="d-flex justify-content-center">
                    <FormGroup check inline>
                      <Label check className="mr-2 text-center">
                        <small>Poor | 1-2 call outs a month</small>
                      </Label>
                      {['1', '2', '3', '4', '5'].map((value: string, i: number) => (
                        <Label check key={`rate_attendance_${i}`} className="ml-2 mr-2 text-center">
                          {value}
                          <br />
                          <Input
                            name="rateAttendance"
                            type="radio"
                            value={value}
                            onChange={() => {}}
                            defaultChecked={parseInt(employee.rateAttendance) === parseInt(value)}
                            disabled={onlyShowMode}
                          />
                        </Label>
                      ))}
                      <Label check className="ml-2 text-center">
                        <small>Best | None</small>
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
              </FormGroup>
            </Col>
            <Col sm={12}>
              <FormGroup className="form-control-label">
                <Label>Number of Performance Improvement Plans</Label>
                <Row>
                  <Col className="d-flex justify-content-center">
                    <FormGroup check inline>
                      {['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map((value: string, i: number) => (
                        <Label check key={`performace_improvement_plan_${i}`} className="ml-2 mr-2 text-center">
                          {value}
                          <br />
                          <Input
                            name="performaceImprovementPlan"
                            type="radio"
                            value={value}
                            onChange={() => {}}
                            defaultChecked={parseInt(employee.performaceImprovementPlan) === parseInt(value)}
                            disabled={onlyShowMode}
                          />
                        </Label>
                      ))}
                    </FormGroup>
                  </Col>
                </Row>
              </FormGroup>
            </Col>
            <Col sm={12}>
              <FormGroup className="form-control-label">
                <Label>
                  Number of Verbal Warnings
                  <br />
                  <small>If any, list policies associated with the verbal warnings</small>
                </Label>
                <Input
                  type="text"
                  name="verbalWarnings"
                  required
                  defaultValue={employee.verbalWarnings}
                  disabled={onlyShowMode}
                />
              </FormGroup>
            </Col>
            <Col sm={12}>
              <FormGroup className="form-control-label">
                <Label>
                  Number of Disciplinary Action Notices
                  <br />
                  <small>If any, list policies with the disciplinary action notices</small>
                </Label>
                <Input
                  type="text"
                  name="disciplinaryActionNotices"
                  required
                  defaultValue={employee.disciplinaryActionNotices}
                  disabled={onlyShowMode}
                />
              </FormGroup>
            </Col>
            <Col sm={12}>
              <FormGroup className="form-control-label">
                <Label>Description</Label>
                <Input
                  type="textarea"
                  name="description"
                  defaultValue={employee.description?.map((d: any) => d.text).join('|')}
                  disabled={onlyShowMode}
                />
              </FormGroup>
            </Col>
            <Col sm={12}>
              <FormGroup className="form-control-label">
                <Label>Take Effect On</Label>
                <InputGroup className="input-group-alternative">
                  <ReactDatetime
                    inputProps={{
                      placeholder: 'Start Date',
                      name: 'effectiveAt',
                      required: true,
                      disabled: onlyShowMode,
                    }}
                    onChange={(newValue) => {
                      setEffectiveAt(`${newValue}`);
                    }}
                    timeFormat={false}
                    value={effectiveAt ? `${moment(effectiveAt).utc().format('MM/DD/yyyy')}` : ''}
                    dateFormat={'MM/DD/yyyy'}
                    closeOnSelect={true}
                    isValidDate={(currentDate) => {
                      const today = moment().startOf('day');
                      const isFuture = currentDate.isAfter(today);
                      const isMonday = currentDate.isoWeekday() === 1;
                      return isFuture && isMonday;
                    }}
                  />
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="ni ni-calendar-grid-58" />
                    </InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
              </FormGroup>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col className="text-right">
              {!onlyShowMode && (
                <Button className="btn-success" type="submit">
                  Save
                </Button>
              )}
            </Col>
          </Row>
        </form>
      )}
    </>
  );
};

export default PayRatesForm;
